import { useEffect, useRef } from 'react';
import { useSelector } from 'react-redux';
import { BE_CALL_ANSWERED, BE_END_CALL } from '@rsos/constants/callStatuses';
import { useFlag } from '@rsos/flags';
import { selectTranscriptionForCall } from '../components/JurisdictionView/EventPane/tabs/TranscriptTab/transcriptionSlice';
import { trackNoTranscriptDisplayed } from '../gainsightTrackings/callTranscriptionTrackings';

/**
 * Custom hook to track cases where the transcript tab is opened but no transcript is displayed
 * when we would expect one to be available (i.e., the call has started).
 *
 * This tracks frequency and patterns for product analytics in Gainsight.
 *
 * The hook waits 10 seconds after tab activation to avoid false positives from initial loading.
 *
 * @param callerId - The ID of the current call
 * @param isTabActive - Whether the transcript tab is currently active
 * @param callStatus - Current status of the call
 * @param callStartTime - Timestamp when the call started
 */
const useTrackMissingTranscript = (
  callerId: string | null,
  isTabActive: boolean,
  callStatus: string | null,
  callStartTime: number | null,
) => {
  const hasTrackedRef = useRef(false);
  const checkTimeoutRef = useRef<NodeJS.Timeout | null>(null);

  // Feature flag to control tracking
  const isTrackingEnabled = useFlag('transcript_missing_tracking');

  // Get transcript data from Redux
  const transcription = useSelector(selectTranscriptionForCall(callerId));
  const tokens = transcription?.payload?.tokens || [];
  const hasTokens = tokens.length > 0;
  const isLoading = transcription?.fetching || false;
  const hasError = transcription?.error !== null;

  useEffect(() => {
    // Skip if tracking is not enabled
    if (!isTrackingEnabled) {
      return;
    }

    // Clear any existing timeout when dependencies change
    if (checkTimeoutRef.current) {
      clearTimeout(checkTimeoutRef.current);
      checkTimeoutRef.current = null;
    }

    // Reset tracking flag when tab becomes inactive or call changes
    if (!isTabActive || !callerId) {
      hasTrackedRef.current = false;
      return;
    }

    // Only track if:
    // 1. Feature flag is enabled
    // 2. Tab is active
    // 3. We haven't already tracked for this tab activation
    // 4. Call is in a state where we'd expect transcripts (answered, ended, or has started)
    const hasCallStatusIndicatingActive =
      callStatus === BE_CALL_ANSWERED || callStatus === BE_END_CALL;
    const hasCallStarted = callStartTime !== null;
    const isCallActive = hasCallStatusIndicatingActive || hasCallStarted;

    if (!isTabActive || hasTrackedRef.current || !isCallActive) {
      return;
    }

    // Set a timeout to check for transcript data after a reasonable delay
    // This accounts for initial loading time and network delays
    const TRANSCRIPT_CHECK_DELAY = 10000; // 10 seconds

    checkTimeoutRef.current = setTimeout(() => {
      // Check if we still don't have transcript data after the delay
      if (!hasTokens && !isLoading) {
        const callState = callStatus === BE_END_CALL ? 'ended' : 'active';

        // Generate a unique correlation ID for tracking across systems (no PII)
        const correlationId = `transcript_missing_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`;

        // Calculate call duration if we have a start time
        let callDuration = 0;
        if (callStartTime) {
          callDuration = Math.floor((Date.now() - callStartTime) / 1000);
        }

        // Determine error type
        let errorType: 'no_data' | 'fetch_error' | 'timeout' = 'no_data';
        if (hasError) {
          errorType = 'fetch_error';
        } else if (callDuration > 60) {
          // If call has been going for over a minute
          errorType = 'timeout';
        }

        // Track in Gainsight for product analytics with correlation ID
        trackNoTranscriptDisplayed({
          callState,
          hasTokens,
          callDuration,
          errorType,
          correlationId,
        });

        // Mark as tracked to avoid duplicate tracking
        hasTrackedRef.current = true;
      }
    }, TRANSCRIPT_CHECK_DELAY);

    // Cleanup function
    return () => {
      if (checkTimeoutRef.current) {
        clearTimeout(checkTimeoutRef.current);
        checkTimeoutRef.current = null;
      }
    };
  }, [
    callerId,
    isTabActive,
    callStatus,
    callStartTime,
    hasTokens,
    isLoading,
    hasError,
    isTrackingEnabled,
    transcription,
  ]);
};

export default useTrackMissingTranscript;
